import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../../AppContext';
import {
  IHistoryConstituent,
  useGetHistoryConstituentsLazyQuery
} from '../../../../generated/graphql';
import moment from 'moment';
import { renderDate } from '../../../../utils/styleUtils';
import { Actions } from '../../../../enums/ActionEnums';
import { exportDataAsExcel } from '../../../../utils/fileUtiles';
import { Box } from '@mui/material';
import InnerLoader from '../../../Common/InnerLoader';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EpraDatePicker from '../../../Common/Fields/EpraDatePicker';
import DenseTable from '../../../Common/Tables/DenseTable';
import { IHeadCell } from '../../../Common/Tables/OverviewTable';

interface IProps {
  stockId: string;
  stockName: string;
}

const ConstituentTab: React.FunctionComponent<IProps> = ({ stockId, stockName }: IProps) => {
  const { dispatch } = useContext(AppContext);
  const [date, setDate] = useState<Date | null>(null);
  const [getConst, { data, loading }] = useGetHistoryConstituentsLazyQuery({
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    getConst({
      variables: {
        stock_id: parseInt(stockId)
      }
    });
  }, []);

  useEffect(() => {
    getConst({
      variables: {
        stock_id: parseInt(stockId),
        date: date ? moment(date).format('YYYY-MM-DD') : null
      }
    });
  }, [date]);

  const exportHistoryConstituents = () => {
    if (data && data.result.length) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const fileName = `${stockName.replaceAll(' ', '-')}-history-constituents-${
        date ? `${moment(date).format('YYYY_MM_DD')}` : 'latest'
      }`;
      const csvData = data.result.map((d) => ({
        'Date Day': renderDate(d?.date as string),
        'Weight Perc': d?.weight_perc,
        Index: d?.index.name,
        'Dividend Yield': d?.history_index?.dividend_yield,
        'Full mkt cap rank': d?.rank
      }));
      exportDataAsExcel(csvData, fileName);
    } else {
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'warning',
          text: 'Missing history constituents data',
          autoHide: 6000
        }
      });
    }
  };

  const columns = [
    {
      title: 'Details',
      sorting: false,
      width: 82
    },
    {
      title: 'Date Day',
      field: 'date',
      width: 167,
      type: 'date',
      render: (rowData: IHistoryConstituent) => renderDate(rowData.date)
    },
    {
      title: 'Weight Perc',
      field: 'weight_perc',
      type: 'numeric',
      width: 178
    },
    {
      title: 'Index',
      width: 809,
      field: 'index.name'
    },
    {
      title: 'DivYield',
      field: 'history_index.dividend_yield',
      type: 'numeric',
      width: 153
    },
    {
      title: 'Full mkt cap rank',
      field: 'rank',
      type: 'numeric',
      width: 153
    }
  ] as IHeadCell[];

  return (
    <Box>
      {loading ? (
        <InnerLoader />
      ) : (
        <Box className="print\:w-full" sx={{ pt: '25px' }}>
          <Box sx={{ position: 'absolute', pl: '25px' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <EpraDatePicker
                id="constituent-date-picker"
                label="Date"
                value={date}
                onChange={(value) => setDate(value)}
              />
            </LocalizationProvider>
          </Box>
          <DenseTable
            columns={columns}
            data={data?.result || []}
            handleDetails={() => true}
            handleRemove={() => true}
            exportButtonProps={{
              id: 'export-constituent-button',
              onClick: exportHistoryConstituents
            }}
            maxHeight={1050}
            pageSize={15}
          />
        </Box>
      )}
    </Box>
  );
};

export default ConstituentTab;
